import { useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiInbox, FiUser } from 'react-icons/fi';

import { useActions, useFetching, useUser } from '@mssgme/hooks';
import { getTeamMembers } from '@mssgme/store';
import { useFilter } from './useFilter';

export function useAssignFilterOptions() {
    const { t } = useTranslation('inbox');
    const user = useUser();
    const { members, statistics } = useSelector((state) => ({
        members: getTeamMembers(state),
        statistics: state.conversations.statistics,
    }));

    const options = useMemo(() => {
        const extractOption = ({ id, name, avatarUrl }) => ({
            label: name,
            value: id,
            meta: {
                avatar: { name: name, src: avatarUrl },
            },
        });

        const notCurrentUser = ({ id }) => id !== user.id;

        return [
            {
                label: t('assignFilter.label.my'),
                value: 'my',
                meta: {
                    avatar: { name: user.name, src: user.avatarUrl },
                    count: statistics.my,
                },
            },
            {
                label: t('assignFilter.label.unassigned'),
                value: 'unassigned',
                meta: {
                    icon: FiUser,
                    count: statistics.unassigned,
                },
            },
            {
                label: t('assignFilter.label.all'),
                value: 'all',
                meta: {
                    icon: FiInbox,
                    count: statistics.all,
                },
            },
            ...members.filter(notCurrentUser).map(extractOption),
        ];
    }, [members, statistics]);

    return options;
}

export default function useAssignFilter() {
    const options = useAssignFilterOptions();
    const [filter, setFilter] = useFilter();
    const { api } = useActions();

    const loadStatistics = useFetching(api.conversations.statistics);

    useEffect(() => {
        loadStatistics.run({ query: { status: filter.status } });
    }, []);

    const onChange = (assign) => setFilter({ assign });

    return { onChange, options, value: filter.assign };
}
